import ExcelJS from 'exceljs';
import {
  SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_EXPORT_TITLE,
  SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_FILE_NAME,
  SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_SHEET_NAME
} from './constant';
import { fetchAllSoChiTietCongNoTheoKhachHang } from '@/hooks/queries/useSoChiTietCongNoTheoKhachHang';
import { SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_COLUMNS } from './column-mapping';
import type { ExcelExportOptions, ExportFromApiOptions } from './types';

const buildFileName = (base: string) => {
  const timestamp = new Date().toISOString().slice(0, 10);
  return `${base}-${timestamp}.xlsx`;
};

const createWorkbook = (sheetName: string) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);
  return { workbook, worksheet };
};

const applyHeaderStyles = (row: ExcelJS.Row) => {
  row.eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
    cell.border = {
      top: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      left: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      right: { style: 'thin', color: { argb: 'FFB0B0B0' } },
      bottom: { style: 'thin', color: { argb: 'FFB0B0B0' } }
    };
  });
};

export const exportSoChiTietCongNoTheoNhaCungCapExcel = async (
  rows: MuaHangRecord[] = [],
  options?: Partial<ExcelExportOptions<MuaHangRecord>>
) => {
  const columns = options?.columns || SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_COLUMNS;
  const title = options?.title || SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_EXPORT_TITLE;
  const sheetName = options?.sheetName || SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_SHEET_NAME;
  const fileName = buildFileName(options?.fileName || SO_CHI_TIET_CONG_NO_THEO_NHA_CUNG_CAP_FILE_NAME);

  const { workbook, worksheet } = createWorkbook(sheetName);

  worksheet.mergeCells(1, 1, 1, columns.length);
  const titleCell = worksheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

  if (options?.subtitle) {
    worksheet.mergeCells(2, 1, 2, columns.length);
    const subtitleCell = worksheet.getCell(2, 1);
    subtitleCell.value = options.subtitle;
    subtitleCell.font = { italic: true, size: 11 };
    subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    worksheet.addRow([]);
  } else {
    worksheet.addRow([]);
  }

  const headerRow = worksheet.addRow(columns.map(col => col.header));
  applyHeaderStyles(headerRow);

  rows.forEach((row, rowIndex) => {
    const rowValues = columns.map(col => {
      if (col.header === 'Tài khoản' && rowIndex < 3) return '';
      const value = col.resolver(row);
      return value ?? '';
    });
    const excelRow = worksheet.addRow(rowValues);

    if (rowIndex < 3) {
      excelRow.eachCell(cell => {
        cell.font = { ...cell.font, bold: true };
      });
    }

    rowValues.forEach((value, colIndex) => {
      if (typeof value === 'number') {
        const cell = excelRow.getCell(colIndex + 1);
        cell.numFmt = '#,##0.00';
      }
    });
  });

  columns.forEach((col, index) => {
    const column = worksheet.getColumn(index + 1);
    column.width = col.width || 18;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();

  URL.revokeObjectURL(url);
};

const formatDateFromApi = (value?: string | null): string | null => {
  if (!value) return null;
  const cleaned = value.replace(/-/g, '');
  if (cleaned.length !== 8) return value;
  const year = cleaned.slice(0, 4);
  const month = cleaned.slice(4, 6);
  const day = cleaned.slice(6, 8);
  return `${day}/${month}/${year}`;
};

export const exportSoChiTietCongNoTheoNhaCungCapFromApi = async ({
  entitySlug,
  queryParams = {},
  pageSize = 200,
  excelOptions
}: ExportFromApiOptions) => {
  const rows = await fetchAllSoChiTietCongNoTheoKhachHang(entitySlug, queryParams, pageSize);

  const from = formatDateFromApi(queryParams.ngay_ct1 ?? null);
  const to = formatDateFromApi(queryParams.ngay_ct2 ?? null);

  let subtitle: string | undefined;
  if (from && to) {
    subtitle = `Từ ngày ${from} đến ngày ${to}`;
  } else if (from) {
    subtitle = `Từ ngày ${from}`;
  } else if (to) {
    subtitle = `Đến ngày ${to}`;
  }

  return exportSoChiTietCongNoTheoNhaCungCapExcel(rows, {
    ...excelOptions,
    subtitle
  });
};
